// Provider/LiteLLM base-URL helpers shared by the Platform screen tabs.

import { PROVIDER_PRESETS } from '../../shared/provider-presets.js';
import type { PlatformProviderKeyProvider, ProviderKeySummary } from '../../types.js';

export const PROVIDERS = Object.keys(PROVIDER_PRESETS) as PlatformProviderKeyProvider[];
export const DEFAULT_LITELLM_BASE_URL = 'http://127.0.0.1:4000';
const SHARED_LITELLM_BASE_URLS = new Set([DEFAULT_LITELLM_BASE_URL, 'http://127.0.0.1:4100']);

export function isSharedLiteLLMBaseUrlSnapshot(baseUrl: string): boolean {
  return SHARED_LITELLM_BASE_URLS.has(baseUrl.trim());
}

/** Preset default base URL for a provider — pre-filled so a (non-developer)
 *  platform admin doesn't have to know each provider's endpoint. Editable. */
export function defaultBaseUrlFor(provider: PlatformProviderKeyProvider): string {
  return PROVIDER_PRESETS[provider]?.defaultBaseUrl ?? '';
}

export function baseUrlsForProvider(
  provider: PlatformProviderKeyProvider,
  keys: ProviderKeySummary[]
): { providerBaseUrl: string; litellmBaseUrl: string } {
  const stored = keys.find((item) => item.provider === provider);
  const storedLiteLLMBaseUrl = stored?.litellmBaseUrl?.trim();
  const litellmBaseUrl =
    storedLiteLLMBaseUrl && !isSharedLiteLLMBaseUrlSnapshot(storedLiteLLMBaseUrl)
      ? storedLiteLLMBaseUrl
      : DEFAULT_LITELLM_BASE_URL;
  // A stored provider base URL that is empty or points at the LiteLLM proxy is
  // a misconfiguration (the provider probe would hit LiteLLM and fail) — fall
  // back to the provider preset default instead of pre-filling the bad value.
  const storedBaseUrl = stored?.baseUrl?.trim();
  const litellmUrls = new Set([litellmBaseUrl.trim(), ...SHARED_LITELLM_BASE_URLS]);
  const providerBaseUrl =
    storedBaseUrl && !litellmUrls.has(storedBaseUrl) ? storedBaseUrl : defaultBaseUrlFor(provider);
  return { providerBaseUrl, litellmBaseUrl };
}
